import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

const services = [
  {
    id: 'kitchen',
    title: 'Kitchen Remodeling',
    description: 'Complete kitchen renovations with modern designs, premium materials, and expert craftsmanship',
    image: 'https://images.unsplash.com/photo-1682888818589-404faaa4dbc9?crop=entropy&cs=srgb&fm=jpg&q=85',
    features: [
      'Custom cabinetry installation',
      'Countertop replacement',
      'Modern lighting fixtures',
      'Flooring installation',
      'Plumbing and electrical work',
    ],
  },
  {
    id: 'bathroom',
    title: 'Bathroom Remodeling',
    description: 'Transform your bathroom into a spa-like retreat with luxury fixtures and elegant design',
    image: 'https://images.unsplash.com/photo-1758448018619-4cbe2250b9ad?crop=entropy&cs=srgb&fm=jpg&q=85',
    features: [
      'Shower and tub installation',
      'Tile work and waterproofing',
      'Vanity and sink installation',
      'Modern fixtures and fittings',
      'Custom storage solutions',
    ],
  },
  {
    id: 'interior',
    title: 'Interior Renovation',
    description: 'Complete interior transformations including drywall, painting, and custom finishes',
    image: 'https://images.unsplash.com/photo-1682888818650-0a7cbb35287d?crop=entropy&cs=srgb&fm=jpg&q=85',
    features: [
      'Drywall installation and repair',
      'Interior painting',
      'Crown molding and trim',
      'Flooring installation',
      'Custom built-ins',
    ],
  },
];

export const Remodeling = ({ onOpenLeadModal }) => {
  const { t } = useTranslation();

  return (
    <div data-testid="remodeling-page">
      {/* Hero Section */}
      <section className="py-32 bg-[hsl(215,75%,16%)] text-white text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1
            className="text-5xl sm:text-6xl font-bold mb-6 tracking-tight"
            style={{ fontFamily: 'Outfit' }}
          >
            Complete Remodeling Services
          </h1>
          <p className="text-xl sm:text-2xl mb-8 leading-relaxed">
            Expert renovation services for kitchens, bathrooms, and interiors
          </p>
          <Button
            onClick={onOpenLeadModal}
            className="bg-white text-[hsl(215,75%,16%)] hover:bg-gray-100 px-12 py-6 text-lg rounded-md font-semibold"
            data-testid="remodeling-hero-cta"
          >
            Get Free Estimate
          </Button>
        </div>
      </section>

      {/* Services Detail Sections */}
      {services.map((service, index) => (
        <section
          key={service.id}
          id={service.id}
          className={`py-24 ${index % 2 === 0 ? 'bg-white' : 'bg-[hsl(210,40%,98%)]'}`}
          data-testid={`remodeling-section-${service.id}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 0 ? '' : 'lg:flex-row-reverse'
              }`}
            >
              {/* Image */}
              <div className={`${index % 2 === 0 ? 'lg:order-1' : 'lg:order-2'}`}>
                <div className="rounded-lg overflow-hidden shadow-2xl">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-96 object-cover"
                    loading="lazy"
                  />
                </div>
              </div>

              {/* Content */}
              <div className={`${index % 2 === 0 ? 'lg:order-2' : 'lg:order-1'}`}>
                <h2
                  className="text-4xl font-bold text-[hsl(215,75%,16%)] mb-6"
                  style={{ fontFamily: 'Outfit' }}
                >
                  {service.title}
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {service.features.map((feature, featureIndex) => (
                    <li
                      key={featureIndex}
                      className="flex items-start text-gray-700"
                    >
                      <svg
                        className="w-6 h-6 text-[hsl(20,91%,48%)] mr-3 flex-shrink-0 mt-0.5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={onOpenLeadModal}
                  className="bg-[hsl(215,75%,16%)] text-white hover:bg-[hsl(224,64%,33%)] px-8 py-6 rounded-md font-semibold"
                  data-testid={`${service.id}-cta-button`}
                >
                  Request Consultation
                </Button>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Final CTA */}
      <section className="py-24 bg-[hsl(215,75%,16%)] text-white text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-4xl font-bold mb-6"
            style={{ fontFamily: 'Outfit' }}
          >
            Ready to Transform Your Space?
          </h2>
          <p className="text-xl mb-8">
            Contact us today to discuss your remodeling project
          </p>
          <Button
            onClick={onOpenLeadModal}
            className="bg-white text-[hsl(215,75%,16%)] hover:bg-gray-100 px-12 py-6 text-lg rounded-md font-semibold"
            data-testid="remodeling-final-cta"
          >
            Get Free Estimate
          </Button>
        </div>
      </section>
    </div>
  );
};